import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Trash2, X, RefreshCw, Upload, Video, Loader, Plus, XCircle, ExternalLink, Coins } from 'lucide-react';
import {
  listContent,
  deleteContent,
  scanContent,
  getContent,
  getJobStatus,
  clearScanStatus,
  getAuthToken
} from '../features/contentCompliance/contentComplianceSlice';
import { selectAuthToken as selectGoogleAuthToken } from '../features/auth/authSlice';
import { asInsufficientCreditsError } from '../features/contentCompliance/contentComplianceService';
import {
  showOutOfCredits,
  fetchScanCost,
  clearScanCost,
  selectScanCost,
  selectScanCostStatus,
  selectEnforcementMode,
} from '../features/billing/billingSlice';
import OutOfCreditsModal from './OutOfCreditsModal';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import instagramIcon from '../assets/insta_icon.png';
import { track, scoreBand } from '../analytics/analytics';
import './MyVideosPage1.css';
import './PlaceholderPage.css';
import '../App.css';

// Facebook icon as inline SVG data URI (no PNG asset available)
const facebookIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>')}`;

// Helper to map job status codes to user-friendly messages
const getStatusDisplay = (status) => {
  const statusMap = {
    'PENDING': { text: 'Starting analysis...', step: 0 },
    'DOWNLOADING': { text: 'Downloading video...', step: 1 },
    'EXTRACTING_AUDIO': { text: 'Extracting audio...', step: 2 },
    'PREPARING_TRANSCRIPTION': { text: 'Preparing transcription...', step: 2 },
    'TRANSCRIBING': { text: 'Transcribing audio...', step: 3 },
    'MERGING_TRANSCRIPTS': { text: 'Processing transcript...', step: 3 },
    'ANALYZING_VIDEO': { text: 'Analyzing video frames...', step: 4 },
    'ANALYZING': { text: 'Running compliance analysis...', step: 5 },
    'COMPLETED': { text: 'Analysis complete!', step: 6 },
    'FAILED': { text: 'Analysis failed', step: -1 },
  };
  return statusMap[status] || { text: 'Processing...', step: 0 };
};

const TOTAL_STEPS = 6;

// How long to wait for a video's duration before allowing an unpriced scan.
// The thumbnail Lambda downloads the whole video before running ffprobe, so
// extraction scales with file size — ~25s for a 23-minute video. The longest
// video in production is 58 minutes (~63s estimated), so this leaves headroom.
const PRICING_WAIT_MS = 90000;

function MyVideosPage1() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Define all available platforms
  const allPlatforms = [
    { name: 'YouTube', icon: youtubeIcon, key: 'youtube' },
    { name: 'Instagram', icon: instagramIcon, key: 'instagram' },
    { name: 'TikTok', icon: tiktokIcon, key: 'tiktok' },
    { name: 'Facebook', icon: facebookIcon, key: 'facebook' }
  ];

  // Redux state
  const contentList = useSelector((state) => state.contentCompliance.contentList);
  const contentListStatus = useSelector((state) => state.contentCompliance.contentListStatus);
  const contentListError = useSelector((state) => state.contentCompliance.contentListError);
  const contentListCursor = useSelector((state) => state.contentCompliance.contentListCursor);
  const contentListHasMore = useSelector((state) => state.contentCompliance.contentListHasMore);
  const scanStatus = useSelector((state) => state.contentCompliance.scanStatus);
  const jobId = useSelector((state) => state.contentCompliance.jobId);
  const jobStatus = useSelector((state) => state.contentCompliance.message);
  const overallScore = useSelector((state) => state.contentCompliance.overall_score);
  const authToken = useSelector((state) => state.contentCompliance.authToken);
  const googleAuthToken = useSelector(selectGoogleAuthToken);

  // Local state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, videoId: null, videoTitle: '' });
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isExpanding, setIsExpanding] = useState(false);
  const [scanConfirm, setScanConfirm] = useState({ show: false, video: null, platformName: '' });
  const [scanningContentId, setScanningContentId] = useState(null);
  const [scanningPlatform, setScanningPlatform] = useState(null);
  const [scanningVideoTitle, setScanningVideoTitle] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showScanProgress, setShowScanProgress] = useState(false);
  const [scanResult, setScanResult] = useState(null); // { score, jobId, status }
  const [maxStepReached, setMaxStepReached] = useState(0); // Track highest step for progress bar
  // Scan couldn't be started (network, server error, ...). Previously these were
  // only console.error'd, so the progress popup just vanished with no feedback.
  const [scanError, setScanError] = useState(null);

  const scanCost = useSelector(selectScanCost);
  const scanCostStatus = useSelector(selectScanCostStatus);
  const enforcementMode = useSelector(selectEnforcementMode);
  // Only show cost when the backend actually charges; in "off" scans are free.
  const showCost = enforcementMode !== 'off';
  // Set when we stop waiting for a price, so a stuck extraction can't leave the
  // user unable to scan at all.
  const [pricingTimedOut, setPricingTimedOut] = useState(false);
  // Scanning is held until the cost is known. Without this the dialog showed
  // "Calculating cost…" while Start Scan stayed clickable, and clicking it ran
  // the scan unpriced — which the backend charges as 0.
  const awaitingPrice = showCost && !scanCost?.ready && !pricingTimedOut;

  // A video's duration is populated asynchronously by the thumbnail Lambda, so
  // right after an upload the cost endpoint can't price the scan yet. Wait for
  // the duration before letting the scan start, otherwise the backend fails open
  // and the scan runs free.
  //
  // Poll GET /api/content/{id} rather than /api/scan/cost: scan/cost re-triggers
  // the thumbnail Lambda on every call where duration is missing, so polling it
  // spawns a duplicate ~25s extraction per poll. The content endpoint is a pure
  // read. Once duration exists, one scan/cost call gets the price and affordability.
  //
  // Gives up after a deadline so a stuck extraction can't block scanning forever;
  // past that we allow the scan and accept the free scan, as before.
  useEffect(() => {
    if (!scanConfirm.show || !showCost) return;
    if (scanCost?.ready) return;              // already priced, nothing to wait for
    if (pricingTimedOut) return;              // gave up; the button is re-enabled
    const contentId = scanConfirm.video?.id;
    if (!contentId) return;

    let cancelled = false;
    const deadline = Date.now() + PRICING_WAIT_MS;

    const tick = async () => {
      while (!cancelled && Date.now() < deadline) {
        try {
          const res = await dispatch(getContent({ contentId })).unwrap();
          if (cancelled) return;
          if (res?.data?.duration_seconds) {
            dispatch(fetchScanCost({ contentId }));
            return;
          }
        } catch (e) {
          if (!cancelled) setPricingTimedOut(true); // don't strand the user on a failed read
          return;
        }
        await new Promise((r) => setTimeout(r, 3000));
      }
      if (!cancelled) setPricingTimedOut(true);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, scanConfirm.show, scanConfirm.video, showCost, pricingTimedOut]);

  // Track if we've fetched content
  const hasFetchedContent = useRef(false);

  // Initialize auth token if needed
  useEffect(() => {
    const storedToken = localStorage.getItem('cleanStudioToken');
    const isTokenValid = (token) => token && token.length > 0 && token !== 'null';

    if (!isTokenValid(storedToken) && !authToken && !googleAuthToken) {
      dispatch(getAuthToken()).unwrap()
        .then((response) => {
          const newToken = response?.data?.access_token;
          if (newToken) {
            localStorage.setItem('cleanStudioToken', newToken);
          }
        })
        .catch(error => {
          console.error("Failed to get auth token:", error);
        });
    }
  }, [dispatch, authToken, googleAuthToken]);

  // Fetch content when we have a valid token
  useEffect(() => {
    const storedToken = localStorage.getItem('cleanStudioToken');
    const effectiveToken = googleAuthToken || authToken || storedToken;
    const isTokenValid = (token) => token && token.length > 0 && token !== 'null';

    if (isTokenValid(effectiveToken) && !hasFetchedContent.current) {
      hasFetchedContent.current = true;
      dispatch(listContent({ limit: 50, sortOrder: 'desc', sortBy: 'created' }));
    }
  }, [dispatch, authToken, googleAuthToken]);

  // Poll job status when scanning
  useEffect(() => {
    let pollInterval;
    if (scanStatus === 'succeeded' && jobId && scanningContentId) {
      pollInterval = setInterval(() => {
        dispatch(getJobStatus(jobId));
      }, 2000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [dispatch, scanStatus, jobId, scanningContentId]);

  // Handle job completion
  useEffect(() => {
    if ((jobStatus === 'COMPLETED' || jobStatus === 'FAILED') && scanningContentId) {
      track(jobStatus === 'COMPLETED' ? 'scan_completed' : 'scan_failed', {
        platform: scanningPlatform || 'unknown',
        score_band: scoreBand(overallScore),
        flow: 'library',
      });
      // Capture result before clearing
      setScanResult({
        score: overallScore,
        jobId: jobId,
        status: jobStatus,
        platform: scanningPlatform,
        videoTitle: scanningVideoTitle,
      });
      // Refresh content list to get updated scores
      hasFetchedContent.current = false;
      dispatch(listContent({ limit: 50, sortOrder: 'desc', sortBy: 'created' }));
      setScanningContentId(null);
      setScanningPlatform(null);
      setScanningVideoTitle('');
      setMaxStepReached(0); // Reset max step
      dispatch(clearScanStatus());
    }
  }, [dispatch, jobStatus, scanningContentId, overallScore, jobId, scanningPlatform, scanningVideoTitle]);

  // Update max step reached (only moves forward, never backward)
  useEffect(() => {
    if (jobStatus && scanningContentId) {
      const currentStep = getStatusDisplay(jobStatus).step;
      if (currentStep > 0) {
        setMaxStepReached(prev => Math.max(prev, currentStep));
      }
    }
  }, [jobStatus, scanningContentId]);

  // Transform API data to component format
  const videos = (contentList || []).map((item) => ({
    id: item.id,
    title: item.title || 'Untitled',
    filename: item.filename || '',
    uploadDate: item.upload_date,
    thumbnail: item.thumbnail,
    type: item.type,
    platforms: (item.platforms || []).map(p => ({
      name: p.name,
      icon: p.name?.toLowerCase() === 'youtube' ? youtubeIcon :
            p.name?.toLowerCase() === 'instagram' ? instagramIcon :
            p.name?.toLowerCase() === 'tiktok' ? tiktokIcon :
            p.name?.toLowerCase() === 'facebook' ? facebookIcon : youtubeIcon,
      score: p.score,
      scanDate: p.scan_date,
      jobId: p.job_id
    }))
  }));

  // Sync selected video with updated content list
  useEffect(() => {
    if (selectedVideo && contentList) {
      const updatedVideo = videos.find(v => v.id === selectedVideo.id);
      if (updatedVideo && JSON.stringify(updatedVideo) !== JSON.stringify(selectedVideo)) {
        setSelectedVideo(updatedVideo);
      }
    }
  }, [contentList, selectedVideo, videos]);

  // Helper function to get platform data for a video
  const getPlatformData = (video, platformName) => {
    return video.platforms.find(p => p.name?.toLowerCase() === platformName.toLowerCase());
  };

  // Format date from ISO string
  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      let normalizedDateString = dateString.trim();

      if (normalizedDateString.includes('T') &&
          !normalizedDateString.endsWith('Z') &&
          !normalizedDateString.match(/[+-]\d{2}:?\d{2}$/)) {
        normalizedDateString = normalizedDateString + 'Z';
      }

      const date = new Date(normalizedDateString);

      if (isNaN(date.getTime())) {
        return '';
      }

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutesStr = minutes < 10 ? '0' + minutes : minutes;

      return `${day} ${month} ${year}, ${hours}:${minutesStr} ${ampm}`;
    } catch (error) {
      return '';
    }
  };

  const handleDeleteClick = (e, video) => {
    e.stopPropagation();
    setDeleteConfirm({
      show: true,
      videoId: video.id,
      videoTitle: video.title
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.videoId) {
      setIsDeleting(true);
      try {
        await dispatch(deleteContent(deleteConfirm.videoId)).unwrap();
        setDeleteConfirm({ show: false, videoId: null, videoTitle: '' });
        // If the deleted video was the selected one, go back to list view
        if (selectedVideo && selectedVideo.id === deleteConfirm.videoId) {
          setSelectedVideo(null);
        }
      } catch (error) {
        console.error('Error deleting content:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, videoId: null, videoTitle: '' });
  };

  const handleScanClick = (e, video, platformName) => {
    e.stopPropagation();
    setScanConfirm({
      show: true,
      video: video,
      platformName: platformName
    });
    // The content already exists here, so we can show the exact credit cost
    // (not the "3-25 credits" estimate) before the user commits.
    dispatch(clearScanCost());
    setPricingTimedOut(false);
    if (video?.id) dispatch(fetchScanCost({ contentId: video.id }));
  };

  const handleScanConfirm = async (video = null, platformName = null) => {
    const scanVideo = video ?? scanConfirm.video;
    const scanPlatform = platformName ?? scanConfirm.platformName;

    if (!scanVideo || !scanPlatform) return;

    // Clear all state first to prevent showing stale results
    dispatch(clearScanStatus());
    setScanResult(null);
    setMaxStepReached(0);
    setScanError(null);

    // Then set new scan state
    setScanningContentId(scanVideo.id);
    setScanningPlatform(scanPlatform.toLowerCase());
    setScanningVideoTitle(scanVideo.title);
    setScanConfirm({ show: false, video: null, platformName: '' });
    setShowScanProgress(true);

    try {
      await dispatch(scanContent({
        contentId: scanVideo.id,
        platform: scanPlatform.toLowerCase()
      })).unwrap();
    } catch (error) {
      console.error('Error starting scan:', error);
      setScanningContentId(null);
      setScanningPlatform(null);
      setScanningVideoTitle('');
      setShowScanProgress(false);

      // Out of credits: surface the top-up modal instead of just closing the
      // progress popup, which left the user with no feedback at all.
      const creditErr = asInsufficientCreditsError(error);
      if (creditErr) {
        dispatch(showOutOfCredits({
          creditsRequired: creditErr.creditsRequired,
          creditsBalance: creditErr.creditsBalance,
          bandLabel: creditErr.bandLabel,
        }));
        return;
      }
      setScanError(error?.message || 'Could not start the scan. Please try again.');
    }
  };

  const handleScanCancel = () => {
    setScanConfirm({ show: false, video: null, platformName: '' });
  };

  const handleCardClick = (video) => {
    setIsExpanding(true);
    setTimeout(() => {
      setSelectedVideo(video);
      setIsExpanding(false);
    }, 300);
  };

  const handleCloseDetail = () => {
    setIsExpanding(true);
    setTimeout(() => {
      setSelectedVideo(null);
      setIsExpanding(false);
    }, 300);
  };

  const handleUploadContent = () => {
    navigate('/dashboard/upload');
  };

  const handleLoadMore = () => {
    if (contentListHasMore && contentListStatus !== 'loading') {
      dispatch(listContent({
        limit: 50,
        cursor: contentListCursor,
        sortOrder: 'desc',
        sortBy: 'created',
        append: true
      }));
    }
  };

  // Check if a specific platform is currently being scanned for a video
  const isScanning = (videoId, platformKey) => {
    return scanningContentId === videoId && scanningPlatform === platformKey;
  };

  // Loading state
  if (contentListStatus === 'loading' && videos.length === 0) {
    return (
      <div className="my-videos-page1">
        <div className="my-videos-loading">
          <Loader size={32} className="spinning" />
          <p>Loading your content...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (contentListError && videos.length === 0) {
    return (
      <div className="my-videos-page1">
        <div className="my-videos-error">
          <p>Error loading content: {contentListError}</p>
          <button onClick={() => {
            hasFetchedContent.current = false;
            dispatch(listContent({ limit: 50, sortOrder: 'desc', sortBy: 'created' }));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (videos.length === 0) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-content">
          <div className="placeholder-icon">
            <Video size={48} />
          </div>
          <h2 className="placeholder-title">Your library is empty</h2>
          <p className="placeholder-description">
            Upload videos or transcripts to scan against different platform guidelines.
          </p>
          <button className="placeholder-cta" onClick={handleUploadContent}>
            <Plus size={18} />
            Upload First Content
          </button>
        </div>
      </div>
    );
  }

  // If a video is selected, show detail view
  if (selectedVideo) {
    // Get fresh data for selected video
    const currentVideo = videos.find(v => v.id === selectedVideo.id) || selectedVideo;

    // Calculate overall stats
    const scannedPlatforms = currentVideo.platforms.filter(p => p.score !== null && p.score !== undefined);
    const avgScore = scannedPlatforms.length > 0
      ? Math.round(scannedPlatforms.reduce((sum, p) => sum + p.score, 0) / scannedPlatforms.length)
      : null;

    return (
      <div className={`my-videos-page1 ${isExpanding ? 'expanding' : 'expanded'}`}>
        <div className="my-videos-detail-view">
          <div className="my-videos-detail-actions">
            <button
              className="my-videos-detail-delete-btn"
              onClick={(e) => handleDeleteClick(e, currentVideo)}
              aria-label="Delete content"
              title="Delete"
            >
              <Trash2 size={20} />
            </button>
            <button
              className="my-videos-close-btn"
              onClick={handleCloseDetail}
              aria-label="Close detail view"
              title="Close"
            >
              <X size={24} />
            </button>
          </div>

          <div className="my-videos-detail-content">
            {/* Left Side */}
            <div className="my-videos-detail-left">
              <div className="my-videos-detail-thumbnail">
                {currentVideo.thumbnail ? (
                  <img src={currentVideo.thumbnail} alt={currentVideo.title} />
                ) : (
                  <div className="my-videos-detail-thumbnail-placeholder">
                    <Video size={48} />
                  </div>
                )}
                <span className="my-videos-detail-type-badge">
                  {currentVideo.type === 'video' ? 'Video' : 'Transcript'}
                </span>
              </div>

              <div className="my-videos-detail-info">
                <h2 className="my-videos-detail-title">{currentVideo.title}</h2>

                <div className="my-videos-detail-meta">
                  <div className="my-videos-detail-meta-row">
                    <span className="my-videos-detail-meta-label">File</span>
                    <span className="my-videos-detail-meta-value">{currentVideo.filename || 'N/A'}</span>
                  </div>
                  <div className="my-videos-detail-meta-row">
                    <span className="my-videos-detail-meta-label">Uploaded</span>
                    <span className="my-videos-detail-meta-value">
                      {formatDate(currentVideo.uploadDate)}
                    </span>
                  </div>
                </div>

                {/* Overall Summary */}
                {scannedPlatforms.length > 0 && (
                  <div className="my-videos-detail-summary">
                    <div className="my-videos-detail-summary-header">
                      <span className="my-videos-detail-summary-label">
                        {scannedPlatforms.length === 1
                          ? `${scannedPlatforms[0].name} Compliance`
                          : 'Overall Compliance'}
                      </span>
                      <span className={`my-videos-detail-summary-score ${avgScore > 70 ? 'high' : avgScore > 40 ? 'medium' : 'low'}`}>
                        {avgScore}%
                      </span>
                    </div>
                    <div className="my-videos-detail-summary-bar">
                      <div
                        className={`my-videos-detail-summary-fill ${avgScore > 70 ? 'high' : avgScore > 40 ? 'medium' : 'low'}`}
                        style={{ width: `${avgScore}%` }}
                      />
                    </div>
                    <p className="my-videos-detail-summary-text">
                      {scannedPlatforms.length === 1
                        ? (avgScore > 70
                            ? `Reviewed for ${scannedPlatforms[0].name}. Check details for any flagged items.`
                            : `Review flagged items before publishing to ${scannedPlatforms[0].name}.`)
                        : (avgScore > 70
                            ? `Reviewed for ${scannedPlatforms.length} platforms. Check details for any flagged items.`
                            : 'Review flagged items before publishing.')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Platform Cards */}
            <div className="my-videos-detail-right">
              <h3 className="my-videos-detail-section-title">Platform Compliance</h3>
              <div className="my-videos-detail-platforms">
                {allPlatforms.map((platform) => {
                  const platformData = getPlatformData(currentVideo, platform.name);
                  const hasData = platformData && platformData.score !== null && platformData.score !== undefined;
                  const scanning = isScanning(currentVideo.id, platform.key);
                  const score = hasData ? platformData.score : 0;

                  // Determine score color class
                  let scoreClass = 'empty';
                  let statusText = 'Not scanned';
                  if (hasData) {
                    if (score > 70) {
                      scoreClass = 'high';
                      statusText = 'Ready to publish';
                    } else if (score > 40) {
                      scoreClass = 'medium';
                      statusText = 'Needs review';
                    } else {
                      scoreClass = 'low';
                      statusText = 'Issues found';
                    }
                  }

                  return (
                    <div key={platform.key} className={`my-videos-platform-card ${hasData ? 'has-data' : ''}`}>
                      <div className="my-videos-platform-card-header">
                        <div className="my-videos-platform-card-icon">
                          <img src={platform.icon} alt={platform.name} />
                        </div>
                        <div className="my-videos-platform-card-info">
                          <span className="my-videos-platform-card-name">{platform.name}</span>
                          <span className={`my-videos-platform-card-status ${scoreClass}`}>
                            {scanning ? 'Scanning...' : statusText}
                          </span>
                        </div>
                        {hasData && (
                          <div className={`my-videos-platform-card-score ${scoreClass}`}>
                            <svg className="my-videos-score-ring" viewBox="0 0 36 36">
                              <path
                                className="my-videos-score-ring-bg"
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              />
                              <path
                                className={`my-videos-score-ring-fill ${scoreClass}`}
                                strokeDasharray={`${score}, 100`}
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              />
                            </svg>
                            <span className="my-videos-score-text">{score}%</span>
                          </div>
                        )}
                      </div>

                      <div className="my-videos-platform-card-actions">
                        {hasData && platformData.scanDate && (
                          <span className="my-videos-platform-card-date">
                            Last scan: {formatDate(platformData.scanDate)}
                          </span>
                        )}
                        <div className="my-videos-platform-card-buttons">
                          {hasData && platformData.jobId && (
                            <button
                              className="my-videos-view-report-btn"
                              onClick={() => navigate(`/dashboard/history?job=${platformData.jobId}`)}
                            >
                              <ExternalLink size={14} />
                              View Report
                            </button>
                          )}
                          <button
                            className="my-videos-scan-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScanConfirm(currentVideo, platform.name);
                            }}
                            disabled={scanning}
                          >
                            {scanning ? (
                              <>
                                <Loader size={14} className="spinning" />
                                Scanning...
                              </>
                            ) : (
                              <>
                                <RefreshCw size={14} />
                                {hasData ? 'Rescan' : 'Scan'}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm.show && (
          <div className="my-videos-modal-overlay" onClick={handleDeleteCancel}>
            <div className="my-videos-modal" onClick={(e) => e.stopPropagation()}>
              <div className="my-videos-modal-header">
                <h3 className="my-videos-modal-title">Delete Content</h3>
                <button
                  className="my-videos-modal-close"
                  onClick={handleDeleteCancel}
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="my-videos-modal-content">
                <p>
                  Are you sure you want to delete <strong>"{deleteConfirm.videoTitle}"</strong>?
                </p>
                <p className="my-videos-modal-warning">
                  This will permanently delete the content and scan history.
                </p>
              </div>
              <div className="my-videos-modal-actions">
                <button
                  className="my-videos-modal-btn my-videos-modal-btn-cancel"
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="my-videos-modal-btn my-videos-modal-btn-delete"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scan Progress/Result Modal */}
        {showScanProgress && (
          <div className="scan-progress-modal-overlay" onClick={() => !scanningContentId && (setShowScanProgress(false), setScanResult(null))}>
            <div className="scan-progress-modal" onClick={(e) => e.stopPropagation()}>
              {!scanningContentId && (
                <button className="scan-progress-modal-close" onClick={() => { setShowScanProgress(false); setScanResult(null); }}>
                  <X size={20} />
                </button>
              )}

              {/* Still processing */}
              {!scanResult && (
                <div className="scan-progress-content">
                  <Loader size={48} className="scan-progress-spinner spinning" />
                  <h3 className="scan-progress-title">Analyzing Your Content</h3>
                  <p className="scan-progress-platform">Platform: {scanningPlatform?.charAt(0).toUpperCase() + scanningPlatform?.slice(1)}</p>
                  <div className="scan-progress-status-box">
                    <span className="scan-progress-status">{getStatusDisplay(jobStatus).text}</span>
                    <div className="scan-progress-bar">
                      <div
                        className="scan-progress-bar-fill"
                        style={{ width: `${(maxStepReached / TOTAL_STEPS) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Completed with results */}
              {scanResult && scanResult.status === 'COMPLETED' && (
                <div className="scan-result-content">
                  <img
                    src={scanResult.platform?.toLowerCase() === 'youtube' ? youtubeIcon : scanResult.platform?.toLowerCase() === 'tiktok' ? tiktokIcon : scanResult.platform?.toLowerCase() === 'facebook' ? facebookIcon : instagramIcon}
                    alt={scanResult.platform}
                    className="scan-result-platform-icon"
                  />
                  <h3 className="scan-result-title">Analysis Complete</h3>
                  {(() => {
                    const s = scanResult.score;
                    const compClass = s > 70 ? 'high' : s > 40 ? 'medium' : 'low';
                    const compLabel = s > 70 ? 'Safe to publish' : s > 40 ? 'Needs review' : 'Issues found';
                    const plt = (scanResult.platform || '').toLowerCase();
                    const adRisk = scanResult.adRevenueRiskLevel;
                    const fyfStat = scanResult.fyfEligibilityStatus;
                    const hasMonetization = (plt === 'youtube' && adRisk) || (plt === 'tiktok' && fyfStat);
                    const monScore = plt === 'youtube' ? scanResult.adRevenueScore : scanResult.fyfScore;
                    const monClass = plt === 'youtube'
                      ? (adRisk === 'low' ? 'high' : adRisk === 'medium' ? 'medium' : 'low')
                      : (fyfStat === 'eligible' ? 'high' : fyfStat === 'at_risk' ? 'medium' : 'low');
                    const monLabel = plt === 'youtube'
                      ? (adRisk === 'low' ? 'Advertiser-friendly' : adRisk === 'medium' ? 'Some sections limited' : 'Not ad-friendly')
                      : (fyfStat === 'eligible' ? 'FYF eligible' : fyfStat === 'at_risk' ? 'FYF at risk' : 'Not FYF eligible');
                    return (
                      <div className="scan-result-scores">
                        <div className="scan-result-score-card">
                          <span className="scan-result-score-card-label">Compliance</span>
                          <span className={`scan-result-score-card-number ${compClass}`}>{Math.round(s)}%</span>
                          <span className={`scan-result-score-card-status ${compClass}`}>{compLabel}</span>
                        </div>
                        {hasMonetization && (
                          <div className="scan-result-score-card">
                            <span className="scan-result-score-card-label">Earnings Compliance</span>
                            <span className={`scan-result-score-card-number ${monClass}`}>{monScore != null ? `${Math.round(monScore)}%` : '—'}</span>
                            <span className={`scan-result-score-card-status ${monClass}`}>{monLabel}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="scan-result-actions">
                    <button className="scan-result-btn secondary" onClick={() => { setShowScanProgress(false); setScanResult(null); }}>
                      Close
                    </button>
                    {scanResult.jobId && (
                      <button className="scan-result-btn primary" onClick={() => {
                        setShowScanProgress(false);
                        setScanResult(null);
                        navigate(`/dashboard/history?job=${scanResult.jobId}`);
                      }}>
                        <ExternalLink size={16} />
                        View Report
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Failed */}
              {scanResult && scanResult.status === 'FAILED' && (
                <div className="scan-result-content">
                  <div className="scan-result-icon error">
                    <XCircle size={48} />
                  </div>
                  <h3 className="scan-result-title">Analysis Failed</h3>
                  <p className="scan-result-error">Scan failed. Please try again.</p>
                  <div className="scan-result-actions">
                    <button className="scan-result-btn secondary" onClick={() => { setShowScanProgress(false); setScanResult(null); }}>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`my-videos-page1 ${isExpanding ? 'expanding' : ''}`}>
      <OutOfCreditsModal />

      {scanError && (
        <div className="my-videos-scan-error" role="alert">
          <XCircle size={16} className="my-videos-scan-error-icon" aria-hidden="true" />
          <span>{scanError}</span>
          <button
            className="my-videos-scan-error-dismiss"
            onClick={() => setScanError(null)}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="my-videos-header">
        <h1 className="my-videos-title">My Library</h1>
        <button className="my-videos-upload-content-btn" onClick={handleUploadContent}>
          <Upload size={20} />
          Upload Content
        </button>
      </div>

      <div className="my-videos-grid">
        {videos.map((video) => (
          <div
            key={video.id}
            className={`my-videos-card ${selectedVideo && selectedVideo.id !== video.id ? 'collapsed' : ''}`}
            onClick={() => handleCardClick(video)}
          >
            <button
              className="my-videos-delete-btn"
              onClick={(e) => handleDeleteClick(e, video)}
              aria-label="Delete video"
              title="Delete video"
            >
              <Trash2 size={18} />
            </button>
            <div className="my-videos-thumbnail">
              {video.thumbnail ? (
                <img src={video.thumbnail} alt={video.title} />
              ) : (
                <div className="my-videos-thumbnail-placeholder">
                  <Video size={32} />
                </div>
              )}
              <span className="my-videos-type-badge">
                {video.type === 'video' ? 'Video' : 'Transcript'}
              </span>
            </div>
            <div className="my-videos-content">
              <h3 className="my-videos-card-title">{video.title}</h3>
              <div className="my-videos-platforms-list">
                {allPlatforms.map((platform) => {
                  const platformData = getPlatformData(video, platform.name);
                  const hasData = platformData && platformData.score !== null && platformData.score !== undefined;
                  const scanning = isScanning(video.id, platform.key);

                  // Determine score color class
                  let scoreClass = 'empty';
                  if (hasData) {
                    const score = platformData.score;
                    if (score > 70) {
                      scoreClass = 'high';
                    } else if (score >= 40) {
                      scoreClass = 'medium';
                    } else {
                      scoreClass = 'low';
                    }
                  }

                  return (
                    <div key={platform.key} className="my-videos-platform-row">
                      <div className="my-videos-platform-info">
                        <img src={platform.icon} alt={platform.name} className="my-videos-platform-icon" />
                        <span className="my-videos-platform-name">{platform.name}</span>
                      </div>
                      {hasData ? (
                        <span className={`my-videos-platform-score ${scoreClass}`}>
                          {platformData.score}%
                        </span>
                      ) : (
                        scanning ? (
                          <Loader size={14} className="spinning" />
                        ) : (
                          <button
                            className="my-videos-platform-scan-btn scan"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScanClick(e, video, platform.name);
                            }}
                          >
                            <RefreshCw size={14} />
                            <span>Scan</span>
                          </button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      {contentListHasMore && (
        <div className="my-videos-load-more">
          <button
            onClick={handleLoadMore}
            disabled={contentListStatus === 'loading'}
          >
            {contentListStatus === 'loading' ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="my-videos-modal-overlay" onClick={handleDeleteCancel}>
          <div className="my-videos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="my-videos-modal-header">
              <h3 className="my-videos-modal-title">Delete Content</h3>
              <button
                className="my-videos-modal-close"
                onClick={handleDeleteCancel}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="my-videos-modal-content">
              <p>
                Are you sure you want to delete <strong>"{deleteConfirm.videoTitle}"</strong>?
              </p>
              <p className="my-videos-modal-warning">
                This will permanently delete the content and scan history.
              </p>
            </div>
            <div className="my-videos-modal-actions">
              <button
                className="my-videos-modal-btn my-videos-modal-btn-cancel"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="my-videos-modal-btn my-videos-modal-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan Confirmation Modal */}
      {scanConfirm.show && (
        <div className="my-videos-modal-overlay" onClick={handleScanCancel}>
          <div className="my-videos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="my-videos-modal-header">
              <h3 className="my-videos-modal-title">Scan Content</h3>
              <button
                className="my-videos-modal-close"
                onClick={handleScanCancel}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="my-videos-modal-content">
              <p>
                Scan <strong>"{scanConfirm.video?.title}"</strong> for <strong>{scanConfirm.platformName}</strong> guidelines?
              </p>
              {showCost && (
                <div className="my-videos-scan-cost">
                  {scanCostStatus === 'loading' && !scanCost && (
                    <span className="my-videos-scan-cost-muted">Calculating cost…</span>
                  )}
                  {scanCost?.ready === false && (
                    <span className="my-videos-scan-cost-muted">
                      Calculating cost… (still reading the video length)
                    </span>
                  )}
                  {scanCost?.ready && (
                    <span className={scanCost.sufficient ? '' : 'my-videos-scan-cost-short'}>
                      <Coins size={14} className="my-videos-scan-cost-icon" aria-hidden="true" />
                      This scan costs <strong>{scanCost.credits_required}</strong> credits
                      {' · '}you have <strong>{scanCost.credits_balance}</strong>
                      {!scanCost.sufficient && ' — not enough'}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="my-videos-modal-actions">
              <button
                className="my-videos-modal-btn my-videos-modal-btn-cancel"
                onClick={handleScanCancel}
              >
                Cancel
              </button>
              {showCost && scanCost?.ready && !scanCost.sufficient ? (
                <button
                  className="my-videos-modal-btn my-videos-modal-btn-confirm"
                  onClick={() => { track('add_credits_clicked', { source: 'library_scan_dialog' }); handleScanCancel(); navigate('/pricing'); }}
                >
                  Add credits
                </button>
              ) : (
                <button
                  className="my-videos-modal-btn my-videos-modal-btn-confirm"
                  onClick={() => handleScanConfirm()}
                  disabled={awaitingPrice}
                >
                  {awaitingPrice ? 'Calculating cost…' : 'Start Scan'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scan Progress/Result Modal */}
      {showScanProgress && (
        <div className="scan-progress-modal-overlay" onClick={() => !scanningContentId && (setShowScanProgress(false), setScanResult(null))}>
          <div className="scan-progress-modal" onClick={(e) => e.stopPropagation()}>
            {!scanningContentId && (
              <button className="scan-progress-modal-close" onClick={() => { setShowScanProgress(false); setScanResult(null); }}>
                <X size={20} />
              </button>
            )}

            {/* Still processing */}
            {!scanResult && (
              <div className="scan-progress-content">
                <Loader size={48} className="scan-progress-spinner spinning" />
                <h3 className="scan-progress-title">Analyzing Your Content</h3>
                <p className="scan-progress-platform">Platform: {scanningPlatform?.charAt(0).toUpperCase() + scanningPlatform?.slice(1)}</p>
                <div className="scan-progress-status-box">
                  <span className="scan-progress-status">{getStatusDisplay(jobStatus).text}</span>
                  <div className="scan-progress-bar">
                    <div
                      className="scan-progress-bar-fill"
                      style={{ width: `${(maxStepReached / TOTAL_STEPS) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Completed with results */}
            {scanResult && scanResult.status === 'COMPLETED' && (
              <div className="scan-result-content">
                <img
                  src={scanResult.platform?.toLowerCase() === 'youtube' ? youtubeIcon : scanResult.platform?.toLowerCase() === 'tiktok' ? tiktokIcon : scanResult.platform?.toLowerCase() === 'facebook' ? facebookIcon : instagramIcon}
                  alt={scanResult.platform}
                  className="scan-result-platform-icon"
                />
                <h3 className="scan-result-title">Analysis Complete</h3>
                                <div className="scan-result-score theme">
                  {Math.round(scanResult.score)}%
                </div>
                <div className="scan-result-actions">
                  <button className="scan-result-btn secondary" onClick={() => { setShowScanProgress(false); setScanResult(null); }}>
                    Close
                  </button>
                  {scanResult.jobId && (
                    <button className="scan-result-btn primary" onClick={() => {
                      setShowScanProgress(false);
                      setScanResult(null);
                      navigate(`/dashboard/history?job=${scanResult.jobId}`);
                    }}>
                      <ExternalLink size={16} />
                      View Report
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Failed */}
            {scanResult && scanResult.status === 'FAILED' && (
              <div className="scan-result-content">
                <div className="scan-result-icon error">
                  <XCircle size={48} />
                </div>
                <h3 className="scan-result-title">Analysis Failed</h3>
                <p className="scan-result-error">Scan failed. Please try again.</p>
                <div className="scan-result-actions">
                  <button className="scan-result-btn secondary" onClick={() => { setShowScanProgress(false); setScanResult(null); }}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MyVideosPage1;
